import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const Services = () => {
  const [activeService, setActiveService] = useState(0);
  const navigate = useNavigate();

  const handleNavigateToServices = () => {
    navigate('/servicios');
    window.scrollTo(0, 0);
  };

  const services = [
    {
      id: 1,
      number: '01',
      title: 'PROGRAMA MUN',
      description: 'Desarrolla debate, oratoria, liderazgo y diplomacia estudiantil. Desarrolla debate oratoria.',
      icon: 'fa-solid fa-gavel',
      image: "mun.png"
    },
    {
      id: 2,
      number: '02',
      title: 'ORIENTACIÓN INTERNACIONAL',
      description: 'Asesoría completa para la admisión en universidades extranjeras de prestigio.',
      icon: 'fa-solid fa-globe',
      image: "orientacion.png"
    },
    {
      id: 3,
      number: '03',
      title: 'DOBLE TITULACIÓN ESCOLAR',
      description: 'Obtén un diploma reconocido internacionalmente junto con tu bachillerato local.',
      icon: 'fa-solid fa-graduation-cap',
      image: "titulacion.png"
    },
    {
      id: 4,
      number: '04',
      title: 'LIDERAZGO GLOBAL',
      description: 'Conecta con líderes jóvenes de todo el mundo y expande tu red profesional.',
      icon: 'fa-solid fa-users',
      image: "liderazgo.png"
    }
  ];

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <>
      {/* Inject FontAwesome for the icons */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      
      <section className="bg-white py-16 px-6 md:px-12 lg:px-16 font-sans">
        <motion.div 
          className="max-w-7xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          
          {/* Header Section */}
          <div className="mb-12">
            <motion.div 
              className="inline-block px-6 py-1.5 border border-gray-400 rounded-full mb-6"
              variants={itemVariants}
            >
              <span className="text-gray-600 text-sm font-medium">Portafolio</span>
            </motion.div>
            <motion.h2 
              className="text-4xl md:text-5xl font-bold text-[#1a3b5c] mb-6"
              variants={itemVariants}
            >
              Nuestros Servicios.
            </motion.h2>
            <motion.p 
              className="text-gray-500 text-lg max-w-2xl font-light"
              variants={itemVariants}
            >
              Programas MUN, doble titulación escolar y asesoría integral para acceso a educación superior en el extranjero.
            </motion.p>
          </div>

          {/* ================= MOBILE LAYOUT (Static Stack) ================= */}
          <motion.div className="flex flex-col gap-8 lg:hidden" variants={containerVariants}>
            {services.map((service) => (
              <motion.div 
                key={service.id} 
                className="bg-white rounded-[2.5rem] overflow-hidden shadow-sm border border-white"
                variants={itemVariants}
              >
                {/* Image Section */}
                <div className="h-64 relative">
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${service.image})` }}
                  />
                  <div className="absolute inset-0 bg-black/10"></div>
                  
                  {/* Mobile Cutout Number */}
                  <div className="absolute bottom-0 right-0 bg-white pl-6 pt-2 pr-4 pb-2 rounded-tl-[2rem]">
                     <span className="text-3xl font-light text-gray-800">
                        {service.number}
                     </span>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-8">
                   <div className="text-gray-900 mb-6">
                      <i className={`${service.icon} text-4xl`}></i>
                   </div>
                   <h3 className="text-xl font-bold text-gray-900 leading-tight mb-3">
                      {service.title}
                   </h3>
                   <p className="text-gray-500 text-sm leading-relaxed mb-6">
                      {service.description}
                   </p>
                   <button onClick={handleNavigateToServices} className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-400 rounded-full text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium w-fit">
                     Ver más
                     <span>→</span>
                   </button>
                </div>
              </motion.div>
            ))}
          </motion.div>


          {/* ================= DESKTOP LAYOUT (Interactive Accordion) ================= */}
          <motion.div 
            className="hidden lg:flex lg:flex-row h-[600px] gap-0 overflow-hidden"
            variants={itemVariants}
          >
            
            {services.map((service, index) => {
              const isActive = activeService === index;

              return (
                <div 
                  key={service.id}
                  onMouseEnter={() => setActiveService(index)}
                  className={`
                    relative cursor-pointer overflow-hidden
                    transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]
                    ${isActive ? 'flex-[15]' : 'flex-[1]'}
                    border-l border-gray-100 first:border-l-0
                  `}
                >
                  
                  {/* --- ACTIVE CONTENT VIEW (Expanded) --- */}
                  <div className={`
                    absolute inset-0 w-full h-full flex flex-row
                    transition-opacity duration-500
                    ${isActive ? 'opacity-100 delay-300 pointer-events-auto' : 'opacity-0 pointer-events-none'}
                  `}>
                    
                    {/* Left Info Column */}
                    <div className="w-[35%] flex flex-col justify-end p-12 z-10 bg-white">
                       <div className="space-y-8">
                          <div className="text-gray-900">
                             <i className={`${service.icon} text-5xl`}></i>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 leading-tight max-w-xs">
                             {service.title}
                          </h3>
                          <p className="text-gray-500 text-sm leading-relaxed">
                             {service.description}
                          </p>
                          <button onClick={handleNavigateToServices} className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-400 rounded-full text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium group w-fit">
                            Ver más
                            <span className="group-hover:translate-x-1 transition-transform">→</span>
                          </button>
                       </div>
                    </div>

                    {/* Right Image Column */}
                    <div className="w-[65%] h-full relative">
                       <div className="w-full h-full rounded-[2.5rem] overflow-hidden relative">
                           <div 
                             className="absolute inset-0 bg-cover bg-center"
                             style={{ backgroundImage: `url(${service.image})` }}
                           />
                           <div className="absolute inset-0 bg-black/10"></div>

                           {/* Cutout Effect */}
                           <div className="absolute bottom-0 right-0 bg-white w-48 h-24 rounded-tl-[2rem] flex items-center justify-center z-10 pl-6 pt-2">
                             <div className="flex flex-col">
                               <span className="text-5xl font-light text-gray-800 leading-none">
                                 {service.number}
                               </span>
                               <span className="text-sm font-semibold text-gray-800 mt-1">
                                 {service.title.split(' ')[0]} {service.title.split(' ')[1]}
                               </span>
                             </div>
                           </div>
                       </div>
                    </div>
                  </div>


                  {/* --- INACTIVE CONTENT VIEW (Collapsed/Vertical Strip) --- */}
                  <div className={`
                    absolute inset-0 w-full h-full bg-white hover:bg-gray-50 flex flex-col items-center justify-center
                    transition-all duration-300
                    ${isActive ? 'opacity-0 pointer-events-none' : 'opacity-100 delay-100 pointer-events-auto'}
                  `}>
                     {/* Vertical Content Wrapper */}
                     <div className="writing-vertical-lr transform rotate-180 flex items-center justify-between gap-8 h-full py-8 w-16">
                        <span className="text-3xl font-light text-gray-300">
                            {service.number}
                        </span>

                        <span className="text-gray-300 font-bold text-lg tracking-widest uppercase whitespace-nowrap">
                            {service.title}
                        </span>

                        <span className="text-gray-300">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                             <path d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                          </svg>
                        </span>
                     </div>
                  </div>

                </div>
              );
            })}
          </motion.div>

        </motion.div>
        
        <style>{`
          .writing-vertical-lr {
            writing-mode: vertical-lr;
          }
        `}</style>
      </section>
    </>
  );
};

export default Services;